/**
 * O cartão da empresa no topo do menu do Logigate: o logótipo, o nome e, quando a pessoa gere
 * mais do que uma, «Trocar», que abre a lista das empresas (com pesquisa quando são muitas).
 * Trocar de empresa é uma página nova (`/{slug}`), não uma visita do Inertia.
 *
 * A lista vem de `/minhas-empresas` uma vez por página e fica em `window._logigate_empresas`
 * (o menu do computador e a gaveta do telemóvel montam cada um o seu cartão).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronsUpDown, LayoutGrid, Loader2, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/Logigate/ui";
import req from "@/utils/req";
import { track } from "@/utils/px";

interface Empresa {
    id: number;
    nome: string;
    foto_link: string;
    slug: string;
    nif?: string | null;
}

interface EmpresaSeletorProps {
    empresaAtual: {
        id: number;
        nome: string;
        foto_link: string;
        slug: string;
    };
}

declare global {
    interface Window {
        _logigate_empresas?: Empresa[];
        _logigate_empresas_loading?: boolean;
    }
}

/** O pedido em curso, partilhado pelos cartões que montam ao mesmo tempo. */
let pedidoEmCurso: Promise<Empresa[] | null> | null = null;

function carregarEmpresas(): Promise<Empresa[] | null> {
    if (window._logigate_empresas) {
        return Promise.resolve(window._logigate_empresas);
    }
    if (!pedidoEmCurso) {
        window._logigate_empresas_loading = true;
        pedidoEmCurso = req()
            .get("/minhas-empresas")
            .then((resposta) => {
                if (resposta?.data?.estado === "ok" && Array.isArray(resposta.data.empresas)) {
                    window._logigate_empresas = resposta.data.empresas;
                    return resposta.data.empresas as Empresa[];
                }
                console.error("Erro ao carregar empresas:", resposta?.data?.texto);
                return null;
            })
            .finally(() => {
                window._logigate_empresas_loading = false;
                pedidoEmCurso = null;
            });
    }
    return pedidoEmCurso;
}

/** Sem acentos e em minúsculas («joao» encontra «João»). */
function semAcentos(t: string): string {
    return t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** A partir de quantas empresas aparece a pesquisa. */
const COM_PESQUISA = 7;

export default function EmpresaSeletor({ empresaAtual }: EmpresaSeletorProps) {
    const [empresas, setEmpresas] = useState<Empresa[]>(window._logigate_empresas || []);
    const [aberto, setAberto] = useState(false);
    const [termo, setTermo] = useState("");
    const [aMudarPara, setAMudarPara] = useState<number | null>(null);
    const caixaPesquisa = useRef<HTMLInputElement>(null);
    const lista = useRef<HTMLUListElement>(null);

    useEffect(() => {
        let vivo = true;
        carregarEmpresas().then((lidas) => {
            if (vivo && lidas) {
                setEmpresas(lidas);
            }
        });
        return () => {
            vivo = false;
        };
    }, []);

    const varias = empresas.length > 1;
    const comPesquisa = empresas.length >= COM_PESQUISA;
    const nif = (empresaAtual as { nif?: string | null }).nif ?? empresas.find((e) => e.id === empresaAtual.id)?.nif;

    const visiveis = useMemo(() => {
        const t = semAcentos(termo.trim());
        if (!t) {
            return empresas;
        }
        return empresas.filter((e) => semAcentos(e.nome).includes(t) || (e.nif ?? "").includes(t));
    }, [empresas, termo]);

    const escolher = (empresa: Empresa) => {
        if (empresa.id === empresaAtual.id) {
            setAberto(false);
            return;
        }
        track("click", "trocar_empresa", { a: "logigate", md: { empresa_id: empresa.id } });
        setAMudarPara(empresa.id);
        window.location.href = `/${empresa.slug}`;
    };

    /** As setas andam pela lista (e da pesquisa para a primeira empresa). */
    const setas = (e: React.KeyboardEvent) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
            return;
        }
        const botoes = Array.from(lista.current?.querySelectorAll<HTMLButtonElement>("button[data-empresa]") ?? []);
        if (botoes.length === 0) {
            return;
        }
        e.preventDefault();
        const i = botoes.indexOf(document.activeElement as HTMLButtonElement);
        const proximo = e.key === "ArrowDown" ? Math.min(botoes.length - 1, i + 1) : i <= 0 ? -1 : i - 1;
        if (proximo < 0) {
            caixaPesquisa.current?.focus();
        } else {
            botoes[proximo].focus();
        }
    };

    const conteudoCartao = (
        <>
            <Avatar
                src={empresaAtual.foto_link}
                nome={empresaAtual.nome}
                redondo={false}
                className="size-9 rounded-lg bg-white ring-1 ring-zinc-950/5 dark:bg-zinc-800 dark:ring-white/10"
            />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold leading-5 text-zinc-900 dark:text-zinc-50">{empresaAtual.nome}</span>
                <span className="block truncate text-xs leading-4 text-zinc-500 dark:text-zinc-400">{nif ? `NIF ${nif}` : "Empresa"}</span>
            </span>
        </>
    );

    const classeCartao = "flex w-full min-w-0 items-center gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50/70 p-2 text-left dark:border-white/10 dark:bg-white/[0.03]";

    if (!varias) {
        return <div className={classeCartao}>{conteudoCartao}</div>;
    }

    return (
        <Popover.Root
            open={aberto}
            onOpenChange={(v) => {
                setAberto(v);
                if (v) {
                    track("click", "empresa_seletor_abrir", { a: "logigate" });
                } else {
                    setTermo("");
                }
            }}
        >
            <Popover.Trigger asChild>
                <button
                    type="button"
                    aria-label={`Empresa: ${empresaAtual.nome}. Trocar de empresa`}
                    className={cn(
                        classeCartao,
                        "group min-h-[52px] pr-2 transition-colors hover:border-zinc-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/30",
                        "data-[state=open]:border-portal/40 data-[state=open]:bg-white data-[state=open]:ring-2 data-[state=open]:ring-portal/15",
                        "dark:hover:border-white/20 dark:hover:bg-white/[0.06] dark:data-[state=open]:border-portal-claro/40 dark:data-[state=open]:bg-white/[0.06]",
                    )}
                >
                    {conteudoCartao}
                    <span className="flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-1 text-xs font-semibold text-portal-texto transition-colors group-hover:bg-portal-suave dark:text-portal-claro dark:group-hover:bg-white/5">
                        Trocar
                        <ChevronsUpDown className="size-3.5" aria-hidden />
                    </span>
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="start"
                    side="bottom"
                    sideOffset={6}
                    collisionPadding={12}
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        if (comPesquisa) {
                            caixaPesquisa.current?.focus();
                        } else {
                            (lista.current?.querySelector<HTMLButtonElement>("button[aria-current='true']") ?? lista.current?.querySelector<HTMLButtonElement>("button[data-empresa]"))?.focus();
                        }
                    }}
                    onKeyDown={setas}
                    className="z-[300] w-[min(320px,calc(100vw-24px))] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl shadow-zinc-950/10 animate-in fade-in-0 zoom-in-95 dark:border-zinc-800 dark:bg-zinc-900"
                >
                    <div className="flex items-center justify-between px-3.5 pb-2 pt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Trocar de empresa</p>
                        <span className="text-xs tabular-nums text-zinc-400">{empresas.length}</span>
                    </div>

                    {comPesquisa && (
                        <div className="px-2 pb-2">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
                                <input
                                    ref={caixaPesquisa}
                                    type="search"
                                    value={termo}
                                    onChange={(e) => setTermo(e.currentTarget.value)}
                                    placeholder="Procurar empresa ou NIF"
                                    aria-label="Procurar empresa"
                                    className="h-10 w-full rounded-lg border border-zinc-200 bg-zinc-50 pl-8 pr-8 text-base text-zinc-800 outline-none transition-colors placeholder:text-zinc-400 focus:border-portal focus:bg-white focus:ring-2 focus:ring-portal/20 md:h-9 md:text-sm dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100 dark:focus:bg-zinc-900 [&::-webkit-search-cancel-button]:hidden"
                                />
                                {termo && (
                                    <button
                                        type="button"
                                        aria-label="Limpar"
                                        onClick={() => {
                                            setTermo("");
                                            caixaPesquisa.current?.focus();
                                        }}
                                        className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-200/70 hover:text-zinc-700 dark:hover:bg-white/10"
                                    >
                                        <X className="size-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <ul ref={lista} className="scroll-bonito max-h-[min(55vh,340px)] space-y-0.5 overflow-y-auto overscroll-contain px-1.5 pb-1.5">
                        {visiveis.length === 0 && (
                            <li className="px-3 py-6 text-center text-[13px] text-zinc-500">Nenhuma empresa com «{termo}».</li>
                        )}
                        {visiveis.map((empresa) => {
                            const actual = empresa.id === empresaAtual.id;
                            const aMudar = aMudarPara === empresa.id;
                            return (
                                <li key={empresa.id}>
                                    <button
                                        type="button"
                                        data-empresa
                                        aria-current={actual ? "true" : undefined}
                                        disabled={aMudarPara !== null}
                                        onClick={() => escolher(empresa)}
                                        className={cn(
                                            "flex min-h-11 w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors focus-visible:outline-none disabled:cursor-wait",
                                            actual
                                                ? "bg-portal-suave dark:bg-white/5"
                                                : "hover:bg-zinc-50 focus-visible:bg-zinc-100 dark:hover:bg-white/5 dark:focus-visible:bg-white/10",
                                            aMudarPara !== null && !aMudar && "opacity-50",
                                        )}
                                    >
                                        <Avatar src={empresa.foto_link} nome={empresa.nome} tamanho="sm" redondo={false} className="rounded-lg bg-white ring-1 ring-zinc-950/5 dark:bg-zinc-800 dark:ring-white/10" />
                                        <span className="min-w-0 flex-1">
                                            <span className={cn("block truncate text-[13px] font-medium", actual ? "text-portal-texto dark:text-white" : "text-zinc-800 dark:text-zinc-100")}>
                                                {empresa.nome}
                                            </span>
                                            {empresa.nif && <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">NIF {empresa.nif}</span>}
                                        </span>
                                        {aMudar ? (
                                            <Loader2 className="size-4 shrink-0 animate-spin text-portal dark:text-portal-claro" aria-label="A abrir" />
                                        ) : actual ? (
                                            <Check className="size-4 shrink-0 text-portal dark:text-portal-claro" aria-hidden />
                                        ) : null}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="border-t border-zinc-100 p-1.5 dark:border-zinc-800">
                        <a
                            href="/empresas"
                            className="flex min-h-10 items-center gap-2 rounded-lg px-2.5 text-[13px] font-medium text-portal-texto transition-colors hover:bg-portal-suave dark:text-portal-claro dark:hover:bg-white/5"
                        >
                            <LayoutGrid className="size-4" aria-hidden />
                            Ver todas as empresas
                        </a>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
